import { useCreationTab } from 'app/screens/new/creation-tab-context';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import React, { useEffect, useRef } from 'react';

import type { TextInput } from 'react-native';
import { Pressable, View } from 'react-native';
import { useNavigation } from 'app/hooks/navigation';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { localeAwareFormat } from 'app/lib/date';
import type { Listing } from 'app/types/listing';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCalendar } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function AvailabilitySection({
  id: listingId,
  form,
  useInventory = false,
}: {
  id?: Listing['id'];

  form: UseFormReturn<any>;
  useInventory?: boolean;
}) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const creationTab = useCreationTab();
  const isRefrain = isWhiteLabel(WhiteLabel.Refrain);

  const transactionMode = form.watch('transactionMode');
  const isDonation = form.getValues('subtype') === ListingSubtype.donation || transactionMode === TransactionMode.give;
  const isSell = transactionMode === TransactionMode.sell;
  const isHauteGaspesieLoan = isWhiteLabel(WhiteLabel.HauteGaspesie) && transactionMode === TransactionMode.lend;
  const tPrefix = isDonation
    ? 'availabilitySection.donation'
    : isHauteGaspesieLoan
      ? 'availabilitySection.loan'
      : 'availabilitySection';

  const blockedDates = form.watch('blockedDates');

  const before = form.watch('before');
  useEffect(() => {
    if (isDonation && before) {
      form.setValue('blockedDates', [before]);
    }
  }, [before, form, isDonation]);

  const goToOfferAvailability = () => {
    navigation.navigate(
      'offer-availability',
      {
        listingId: listingId,
        goBack: true,
        returnToPublish: creationTab,
        blockedDates,
        singleDate: isDonation || isSell,
        singleDateType: isDonation ? 'donation' : isSell ? 'sell' : undefined,
      },
      { pop: true },
    );
  };

  return (
    <View
      style={{
        backgroundColor: isRefrain ? theme.colors['surface.secondary-neutral'] : undefined,
        borderColor: theme.colors['border.secondary'],
        borderRadius: theme.borderRadii['radius.xl'],
        borderWidth: isRefrain ? 0 : theme.borderWidths['border-width.subtle'],
        paddingHorizontal: 20,
        paddingVertical: 25,
      }}
    >
      <Text fontWeight="600" textAlign="center" variant="headerXSmall">
        {t('availabilitySection.title', { ns: 'one-listing' })}
      </Text>

      <Pressable
        onPress={goToOfferAvailability}
        style={{
          backgroundColor: theme.colors['surface.default'],
          borderRadius: theme.borderRadii['radius.lg'],
          marginTop: theme.spacing['spacing.general.x4'],
          padding: theme.spacing['spacing.general.x4'],
        }}
      >
        <Row alignItems="center" flex={1}>
          <Column flex={1} gap="spacing.general.x1" minWidth={0}>
            <Text variant="bodyBase">
              <Text fontWeight="700" variant="bodyBase">
                {t(`${tPrefix}.reservations.title`, { ns: 'one-listing' })}
              </Text>
              {` (${t(`${tPrefix}.reservations.optionalLabel`, {
                ns: 'one-listing',
                defaultValue: t(isDonation ? 'optionale' : 'optionales', { ns: 'common' }),
              })})`}
            </Text>
            <Text color="text.secondary">
              {isDonation && blockedDates?.length > 0
                ? localeAwareFormat(new Date(blockedDates[0] as string), t('dateFormat_MMMMdYYYY', { ns: 'common' }))
                : t(`${tPrefix}.reservations.text`, { ns: 'one-listing' })}
            </Text>
          </Column>
          <Column alignItems="flex-end" marginStart="spacing.general.x2">
            <IconCalendar strokeWidth={ICON_STROKE_WIDTH} size={32} />
          </Column>
        </Row>
      </Pressable>

      <InventoryField form={form} marginTop useInventory={useInventory} />
    </View>
  );
}

export function InventoryField({
  form,
  marginTop = false,
  useInventory = true,
}: {
  form: UseFormReturn<any>;
  marginTop?: boolean;
  useInventory?: boolean;
}) {
  const { t } = useTranslation();
  const valueInputRef = useRef<TextInput>(null);
  const { isSmallUI } = useUISize();

  const handleQuantityChange = (value: string | null) => {
    if (!useInventory && !isMobileNative) {
      const numericValue = value?.replace(/[^0-9]/g, '') || '';
      form.setValue('quantity', numericValue || null);
    }
  };

  return (
    <Pressable
      onPress={() => valueInputRef.current?.focus()}
      style={{
        backgroundColor: theme.colors['surface.default'],
        borderRadius: theme.borderRadii['radius.lg'],
        marginTop: marginTop ? theme.spacing['spacing.general.x4'] : undefined,
        padding: theme.spacing['spacing.general.x4'],
      }}
    >
      {isSmallUI ? (
        <Column gap="spacing.general.x3" width="100%">
          <Column gap="spacing.general.x1" minWidth={0}>
            <Text variant="bodyBase">
              <Text fontWeight="700" variant="bodyBase">
                {t('availabilitySection.quantity.title', { ns: 'one-listing' })}
              </Text>
              {` (${t('optional', { ns: 'common' })})`}
            </Text>
            <Text color="text.secondary">{t('availabilitySection.quantity.text', { ns: 'one-listing' })}</Text>
          </Column>
          <ControlledInput
            {...{ control: form.control }}
            innerRef={valueInputRef}
            keyboardType={useInventory ? undefined : 'number-pad'}
            name={useInventory ? 'inventory' : 'quantity'}
            nullable
            placeholder={t('availabilitySection.quantity.fieldPlaceholder', { ns: 'one-listing' })}
            style={{ minHeight: 49 }}
            textAlign="center"
            onChangeText={useInventory ? undefined : handleQuantityChange}
          />
        </Column>
      ) : (
        <Row alignItems="center" flex={1}>
          <Column flex={1} gap="spacing.general.x1" minWidth={0}>
            <Text variant="bodyBase">
              <Text fontWeight="700" variant="bodyBase">
                {t('availabilitySection.quantity.title', { ns: 'one-listing' })}
              </Text>
              {` (${t('optional', { ns: 'common' })})`}
            </Text>
            <Text color="text.secondary">{t('availabilitySection.quantity.text', { ns: 'one-listing' })}</Text>
          </Column>
          <Column flex={1} maxWidth={160} minWidth={0}>
            <ControlledInput
              {...{ control: form.control }}
              innerRef={valueInputRef}
              keyboardType={useInventory ? undefined : 'number-pad'}
              name={useInventory ? 'inventory' : 'quantity'}
              nullable
              placeholder={t('availabilitySection.quantity.fieldPlaceholder', { ns: 'one-listing' })}
              style={{ minHeight: 49 }}
              textAlign="center"
              onChangeText={useInventory ? undefined : handleQuantityChange}
            />
          </Column>
        </Row>
      )}
    </Pressable>
  );
}
